/**
 * Deterministic date extraction, on the wire.
 *
 * Free's whole promise here is that a task typed in English gets its date
 * filled in without a paid provider call. Which means the contract has to make
 * three failure modes impossible to express, not merely discouraged:
 *
 *  - **A parser cannot invent certainty.** `date` and `time` each carry their
 *    own certainty flag. A date-only phrase — "next Friday" — returns a date
 *    with no time and `time_certain: false`, never noon. Noon is a deadline
 *    somebody will be late for.
 *  - **A parser cannot rewrite the past.** `forward_date` is applied only to
 *    ambiguous or missing future components. "Last Tuesday" stays last
 *    Tuesday, and the shape has no field that could say otherwise.
 *  - **A parser cannot decide anything but time.** There is no title, no
 *    assignee, no priority, no proof and no second task in the response. Those
 *    are AI features with an allowance; this one is free because it does
 *    nothing that costs money.
 *
 * Unsupported locales return `status: 'unsupported_locale'` with no result,
 * not a guess. chrono-node handles English; Hebrew, Arabic, Urdu and Tagalog
 * are not promised and must never silently appear to work.
 */
import { z } from 'zod';
import { IsoDate, IsoDatetime } from './primitives.js';

/**
 * `POST /v1/date-intent/parse`.
 *
 * `reference_instant` is required rather than defaulted to the server's clock:
 * "tomorrow" typed at 23:50 in Dubai is a different day in UTC, and a server
 * that supplies its own now has already got it wrong for half the households.
 */
export const ParseDateIntentRequest = z.object({
  /** The text the person typed. Never stored by this route — see
   *  temporal-research.ts for the only thing that is, and only with consent. */
  text: z.string().trim().min(1).max(2000),
  /** The household's timezone, explicitly. Not inferred, not defaulted. */
  timezone: z.string().min(1).max(64),
  reference_instant: IsoDatetime,
  /** BCP-47-ish. Anything but English returns unsupported_locale. */
  locale: z.string().min(2).max(16).default('en'),
});
export type ParseDateIntentRequest = z.infer<typeof ParseDateIntentRequest>;

export const DateIntentStatus = z.enum([
  /** A confident single date, and possibly a time. */
  'parsed',
  /** Something was found but is ambiguous enough to need confirmation. The
   *  client shows it as a suggestion, never as an applied value. */
  'review',
  /** Nothing temporal in the text. Not an error. */
  'no_parse',
  /** The locale is not one chrono-node is trusted for. Also not an error, and
   *  never a reason to fall back to a paid model on Free. */
  'unsupported_locale',
]);
export type DateIntentStatus = z.infer<typeof DateIntentStatus>;

/**
 * One extracted date or range.
 *
 * `date_certain` / `time_certain` distinguish what was stated from what was
 * implied — chrono's own distinction, preserved rather than flattened, because
 * flattening it is exactly how "sometime Friday" becomes a 12:00 deadline.
 */
export const DateIntentResult = z.object({
  date: IsoDate.nullable(),
  /** Local wall clock in the household's timezone, `HH:MM`. Null when the
   *  phrase named no time — never filled in with a default. */
  time: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable(),
  date_certain: z.boolean(),
  time_certain: z.boolean(),
  /** The end of a range, when the phrase named one ("Monday to Wednesday"). */
  end_date: IsoDate.nullable(),
  end_time: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable(),
  /** True when `forwardDate` moved an ambiguous component into the future.
   *  Never true for an explicitly stated past date. */
  forward_date_applied: z.boolean(),
  /** Offsets into the submitted text. The client highlights the phrase it
   *  understood, which is the only honest way to show a parse. */
  start_offset: z.number().int().nonnegative(),
  end_offset: z.number().int().nonnegative(),
  /** The exact substring those offsets cover. Short by construction. */
  phrase: z.string().max(120),
});
export type DateIntentResult = z.infer<typeof DateIntentResult>;

export const ParseDateIntentResponse = z.object({
  status: DateIntentStatus,
  result: DateIntentResult.nullable(),
  /** Pinned so a stored research example can say which parser produced it and
   *  a behaviour change is attributable rather than mysterious. */
  parser_version: z.string().max(40),
  /** Always false on this route. Present so a caller can assert it, and so a
   *  future paid enrichment path cannot be introduced without the response
   *  admitting it. */
  used_paid_provider: z.literal(false),
});
export type ParseDateIntentResponse = z.infer<typeof ParseDateIntentResponse>;

/** Locales the deterministic parser is trusted for. English only, and stated
 *  as data so nothing has to remember the list. */
export const SUPPORTED_DATE_INTENT_LOCALES: readonly string[] = ['en'];

export function isDateIntentLocaleSupported(locale: string): boolean {
  return SUPPORTED_DATE_INTENT_LOCALES.includes(locale.slice(0, 2).toLowerCase());
}
